import React from "react";
import { Type, Star, Gauge, Hash, List, Smile, ToggleLeft, MapPin, MessageSquareText } from "lucide-react";
import { ContentPage, EligibilityCriterion, Fan, Player } from "@/services/bedrock";
import { FormRouteSource } from "./FormRoutes";

export interface FormInfo {
  displayName: string;
  fanIdentityCollection: "hidden" | "optional" | "required";
  toneOfVoice: "professional" | "warm" | "casual" | "urgent";
  allowAiPersonalization: boolean;
  introTitle?: string;
  introText?: string;
  outroTitle?: string;
  outroText?: string;
  formFields: FormFieldInfo[];
}

export type FormFieldType =
  | "nps"
  | "ten_point"
  | "star"
  | "text"
  | "long_text"
  | "single_choice"
  | "boolean"
  | "sentiment"
  | "state";

export const FIELD_TYPE_MAP: Record<FormFieldType, { label: string; icon: React.ComponentType<any> }> = {
  text: { label: "Text", icon: Type },
  long_text: { label: "Paragraph", icon: MessageSquareText },
  star: { label: "Rating (Stars)", icon: Star },
  nps: { label: "NPS", icon: Gauge },
  ten_point: { label: "10-Point Score", icon: Hash },
  single_choice: { label: "Choice", icon: List },
  sentiment: { label: "Emojis", icon: Smile },
  boolean: { label: "Yes/No", icon: ToggleLeft },
  state: { label: "State", icon: MapPin },
};

// Bedrock marks a review as approved to share from the answer to the field with this key
export const SOCIAL_SHARING_CONSENT_KEY = "social_sharing_consent";

interface FormFieldInfo {
  fieldKey: string;
  title: string;
  subtitle?: string;
  fieldType: FormFieldType;
  required: boolean;
  position: number;
  fieldOptions?: {
    choices?: string[];
    collectionMode?: "first_party_only" | "third_party_only" | "hybrid";
    handoffTiming?: "on_completion" | "inline";
    internalTextPrompt?: string;
  };
}

export type LanguageCode = "en" | "es";

export interface Form extends FormInfo {
  id: number;
  guid: string;
  active: boolean;
  contentPageThemeId: number | null;
  allowThemeChange: boolean;
  allowTranslations: boolean;
  supportedLanguages: LanguageCode[];
  translationStatus: "pending" | "completed" | "failed";
  translations?: Record<LanguageCode, any>;
  formFields: Array<FormField>;
  outroCtaLabel: string | null;
  outroCtaType: FormCtaType | null;
  outroCtaLink: string | null;
  showSocialsOnOutro: boolean | null;
  testRequest: FormRequest | null;
  totalRequestCount?: number;
  totalResponseCount?: number;
  eligibilityCriterions?: EligibilityCriterion[];
}

export type FormCtaType =
  | "custom_link"
  | "custom_phone"
  | "custom_email"
  | "player_link"
  | "player_phone"
  | "player_email"
  | "share_player_link";

interface FormField extends FormFieldInfo {
  id: number;
  active: boolean;
}

export interface FormRequest {
  id: number;
  formId: number;
  formDisplayName: string;
  shortCode: string;
  status: "pending_ai" | "ready" | "opened" | "completed" | "failed";
  internalIdentifier: string | null;
  sourceSystem?: FormRouteSource | null;
  sourceCategory?: "manual" | "api" | "route" | null;
  /** Keys inside arrive camelCased, e.g. `route.resolvedAt`. */
  attribution?: { utm?: Record<string, string>; route?: Record<string, string> } | null;
  url: string;
  isTest: boolean;
  useAiPersonalization: boolean;
  contextInstructions: string | null;
  aiFieldOverrides: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
  form?: Form;
  player?: Player;
  fan?: Fan;
  totalResponseCount?: number;
  latestContentPage?: ContentPage;
}

export type FormResponseStatus = "initialized" | "in_progress" | "completed";

export interface FormResponse {
  id: number;
  isTest: boolean;
  status: FormResponseStatus;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  fan?: Fan;
  player?: Player;
  /** What the visitor typed on the form, exactly as typed. */
  submittedIdentity?: { displayName?: string; email?: string; phone?: string; language?: string } | null;
  /** The form link it came in on, with where that link came from. Only with `extraFields: ["formRequestSource"]`. */
  formRequestSource?: Pick<
    FormRequest,
    "id" | "shortCode" | "url" | "internalIdentifier" | "sourceSystem" | "sourceCategory" | "attribution"
  >;
  formFieldResponses: FormFieldResponse[];
}

interface FormFieldResponse {
  id: number;
  value: any;
  createdAt: string;
  updatedAt: string;
  formField?: FormField;
}

export type FormDefaultType = "review" | "midpoint" | "bad_review_feedback";

export interface FormDefault {
  id: number;
  formType: FormDefaultType;
  formId: number;
  coachId: number;
  segmentationDetailId: number;
  form: Form;
}
